import numberUtil from "@plannel/utils/number-util";

// LLM 토큰 사용량 화면 공용 — 표시 포맷·코드 라벨. 코드값은 제공자 비종속(백엔드 원장 코드 그대로).

export const TOKEN_KEYS = ["input_tokens", "output_tokens", "cache_read_tokens", "cache_write_tokens"];

export const TOKEN_LABEL_KEYS = {
  input_tokens: "llmUsageInputTokens",
  output_tokens: "llmUsageOutputTokens",
  cache_read_tokens: "llmUsageCacheReadTokens",
  cache_write_tokens: "llmUsageCacheWriteTokens",
};

// 토큰 구성비 카드용 짧은 라벨 — 제목이 이미 토큰이라 "토큰"을 뺀다
export const TOKEN_TYPE_LABEL_KEYS = {
  input_tokens: "llmUsageTokenTypeInput",
  output_tokens: "llmUsageTokenTypeOutput",
  cache_read_tokens: "llmUsageTokenTypeCacheRead",
  cache_write_tokens: "llmUsageTokenTypeCacheWrite",
};

// 단가 4열 — 단가 탭과 단가 가져오기 창이 같이 쓴다. "100만 토큰당"은 그룹 헤더에만 두고 열은 짧은 라벨
export const PRICE_AMT_FIELDS = ["input_mtok_amt", "output_mtok_amt", "cache_read_mtok_amt", "cache_write_mtok_amt"];
export const PRICE_AMT_LABEL_KEYS = {
  input_mtok_amt: TOKEN_TYPE_LABEL_KEYS.input_tokens,
  output_mtok_amt: TOKEN_TYPE_LABEL_KEYS.output_tokens,
  cache_read_mtok_amt: TOKEN_TYPE_LABEL_KEYS.cache_read_tokens,
  cache_write_mtok_amt: TOKEN_TYPE_LABEL_KEYS.cache_write_tokens,
};
// 라벨 길이에 맞춘 폭 — 캐시 2열은 ja "キャッシュ読み取り" 가 한 줄에 들어가는 폭
export const PRICE_AMT_WIDTHS = {
  input_mtok_amt: 100, output_mtok_amt: 100, cache_read_mtok_amt: 150, cache_write_mtok_amt: 150,
};

export const FEATURES = ["CHAT", "SCENARIO", "CUSTOM_SCENARIO", "MARKETING", "UNATTRIBUTED"];
export const MODULES = ["BF", "DP", "IP", "RP", "MP", "FP", "GENERAL"];
export const OPERATIONS = ["generate", "embedding"];
// 원장 status_cd 는 ok/error 두 값
export const STATUSES = ["ok", "error"];

const FEATURE_LABEL_KEYS = {
  CHAT: "llmUsageFeatureChat",
  SCENARIO: "menuAiScenarioPlanning",
  CUSTOM_SCENARIO: "llmUsageFeatureCustomScenario",
  MARKETING: "menuAiMarketingIntelligence",
  UNATTRIBUTED: "llmUsageFeatureUnattributed",
};

const OPERATION_LABEL_KEYS = {
  generate: "llmUsageOperationGenerate",
  embedding: "llmUsageOperationEmbedding",
};

// json-bigint 가 큰 정수를 BigInt 로 줄 수 있어 표시 전에 Number 로 정규화
export const num = (v) => (v == null || v === "" ? 0 : Number(String(v)) || 0);

// 표기 로케일은 앱 공용 numberUtil(en 고정)을 따른다 — 브라우저 로케일마다 구분자가 달라지지 않게
export const fmtInt = (v) => numberUtil.formatNumber(num(v), { maximumFractionDigits: 0 });

export const fmtAmt = (v) =>
  numberUtil.formatNumber(num(v), { minimumFractionDigits: 2, maximumFractionDigits: 4 });

// 카드 표시용 고정 자리수 — 합계와 호출당 금액의 자리수를 화면에서 통일한다
export const fmtFixed = (v, digits) =>
  numberUtil.formatNumber(num(v), { minimumFractionDigits: digits, maximumFractionDigits: digits });

// 비용 자릿수 — 소수 없는 통화(JPY·KRW 등)는 정수, 그 외는 호출당 소액이 보이도록 4자리
const currencyDigits = (currency) => {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits === 0 ? 0 : 4;
  } catch {
    return 4;
  }
};

export const fmtCost = (v, currency) => fmtFixed(v, currencyDigits(currency));

// 통화 코드 + 금액 — 기호는 나라마다 겹쳐($·¥) 코드로 쓴다
export const fmtBase = (v, currency) => `${currency} ${fmtCost(v, currency)}`;

// 요약의 한 집계(kpi·일자·내역 행)에 붙일 기본 통화 환산 문구. 환산 불가·동일 통화면 null
export const baseCostText = (baseCurrency, acc) => {
  const costs = acc?.costs || [];
  if (!baseCurrency || acc?.base_cost_amt == null || costs.length === 0) return null;
  if (costs.every((c) => c.currency_cd === baseCurrency)) return null;
  return `(${fmtBase(acc.base_cost_amt, baseCurrency)})`;
};

export const fmtDec1 = (v) =>
  numberUtil.formatNumber(num(v), { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// 추이 차트의 비용선 필드 — 토큰 4종 뒤에 오는 계열이라 색 순번도 그 다음이다
export const COST_FIELD = "cost";
export const SERIES_FIELDS = [...TOKEN_KEYS, COST_FIELD];

// 차트·표 공용 색 토큰 — 토큰 계열은 밝은 중명도 색상 구분 팔레트, 비용선은 활성 메뉴 오렌지(별도 축 구분)
export const chart = {
  token: {
    // 단일 색상 명도 램프는 인접 단계 구분이 안 돼 색상을 나눈다 — 면적이 큰 캐시 읽기에 틸을 둬 화면 톤을 유지
    input: "#64A0DC", output: "#E57373", cacheRead: "#3DB2C4", cacheWrite: "#8C7BD6", cost: "#F5A623",
  },
  grid: "#EEF1F3", axisText: "#7B919C", barFill: "#3DB2C4", barTrack: "#EAF6F8", pointFill: "#FFFFFF",
};

// 개요 탭 전용 팔레트 — 강조색은 차트 기준 틸을 따르고, 글자용은 대비 4.5:1 을 넘기게 같은 색상을 어둡게 쓴다
export const UC = {
  text1: "#1d1d1f", text2: "#6e6e73", text3: "#86868b", text4: "#a1a1a6",
  card: "#ffffff", border: "rgba(0,0,0,0.07)", line: "rgba(0,0,0,0.10)",
  grid: "rgba(0,0,0,0.06)", hover: "rgba(0,0,0,0.02)", footer: "#fbfbfd",
  acc: "#1C8293", accBg: chart.barTrack, accBorder: "rgba(61,178,196,0.30)", accText: "#1C8293",
};

const SERIES_COLORS = {
  input_tokens: chart.token.input, output_tokens: chart.token.output, cache_read_tokens: chart.token.cacheRead,
  cache_write_tokens: chart.token.cacheWrite, [COST_FIELD]: chart.token.cost,
};

export const seriesColor = (field) => SERIES_COLORS[field];

export const CARD_SX = {
  bgcolor: UC.card, border: `1px solid ${UC.border}`, borderRadius: "10px", p: "14px 16px",
  display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, boxSizing: "border-box",
};

// 숫자는 자릿수를 세로로 맞춘다 — 값이 바뀌어도 열이 흔들리지 않게
export const NUM_SX = { fontVariantNumeric: "tabular-nums" };

export const featureLabel = (t, code) => (FEATURE_LABEL_KEYS[code] ? t(FEATURE_LABEL_KEYS[code]) : code || "-");

export const operationLabel = (t, code) => (OPERATION_LABEL_KEYS[code] ? t(OPERATION_LABEL_KEYS[code]) : code || "-");

// 알 수 없는 상태값은 성공으로 뭉개지 않고 코드 그대로 보여 준다
export const statusLabel = (t, code) => (code === "error" ? t("error") : code === "ok" ? t("success") : code || "-");

// 모듈이 기록되지 않은 호출(원장 값 "-")은 기호 대신 문구로
export const moduleLabel = (t, code) => (!code || code === "-" ? t("llmUsageNotSet") : code);

// 원장은 z_user.id 를 보관하고 표시는 서버가 붙인 로그인ID(user_name) — 매칭 안 되면 ID 그대로. 0 = 시스템
export const userLabel = (t, id, name) => {
  if (name) return name;
  if (id === 0 || id === "0") return t("llmUsageSystemUser");
  return id ?? "-";
};

export const dateStr = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${mm}-${dd}`;
};

// UTC 저장 시각("...Z") → 브라우저 로컬 시각 문자열
export const localTs = (ts) => {
  if (!ts) return "";
  const x = new Date(ts);
  return Number.isNaN(x.getTime()) ? String(ts) : `${dateStr(x)} ${x.toTimeString().slice(0, 8)}`;
};
